import React, { useEffect, useRef } from 'react';

import { EmailCaptureSheet } from '../board/EmailCaptureSheet';
import { markEmailCaptured } from '../lib/emailCaptureState';
import { gaEmailCaptureShown, gaEmailCaptureDismissed } from '../lib/gaPixel';

/**
 * The Layer-2 persistent "Save your boards" entry point (tranche 2 of the
 * OTP-auth spec). Reuses the board's `EmailCaptureSheet` verbatim — this
 * container only owns the plumbing the two account hosts (the desktop
 * `HeaderAccountMenu` dropdown + the mobile You tab) share when they reopen
 * the sheet on demand:
 *
 *   - the `theaWebCaptureEmail` callable wiring (same as the board host),
 *   - marking the shared captured flag on success (so neither surface — nor
 *     the board trigger — ever prompts again), and
 *   - the GA lifecycle with `surface: 'account'` so self-initiated captures
 *     (the highest-converting path) are distinguishable from the board's
 *     dwell-triggered ones.
 *
 * WHEN it opens is the host's decision (`open`); this is a controlled sheet.
 * A single `onClose` collapses the host's open state on either exit path
 * (dismiss OR capture) — GA + the captured flag are handled here.
 */

const AFFORDANCE_SURFACE = 'account';

export interface SaveBoardsSheetProps {
  open: boolean;
  /** The board name to echo in the headline ("Don't lose {name}'s board").
   *  In production the caller passes their most-recent recipient's name. */
  recipientName: string;
  /** The recipient the re-engagement email should deep-link back to. */
  recipientId?: string;
  /** The recipient's active recommendation (`recipient.currentRecommendationId`).
   *  With `recipientId` it forms the recipient's BOARD URL
   *  (`/quiz/results/{recipientId}/{recommendationId}`) — the link the email
   *  should point at, so it lands where the resume-token redeem leg is mounted
   *  (see onSubmit). Absent → the emailed link falls back to the current URL. */
  recommendationId?: string;
  /** Collapse the host's open state — fired on dismiss AND after capture. */
  onClose: () => void;
}

export const SaveBoardsSheet: React.FC<SaveBoardsSheetProps> = ({
  open,
  recipientName,
  recipientId,
  recommendationId,
  onClose,
}) => {
  // Fire the `shown` GA once per open transition (mirrors the board trigger,
  // which fires shown when it opens the sheet). A ref guards against a
  // re-render re-firing while the sheet stays open.
  const shownRef = useRef(false);
  useEffect(() => {
    if (open && !shownRef.current) {
      shownRef.current = true;
      gaEmailCaptureShown({ surface: AFFORDANCE_SURFACE });
    }
    if (!open) shownRef.current = false;
  }, [open]);

  return (
    <EmailCaptureSheet
      open={open}
      surface={AFFORDANCE_SURFACE}
      recipientName={recipientName}
      onSubmit={async (email) => {
        // Lazy-load the beacon + the resume-token decorator so their
        // firebaseConfig pull stays out of this component's static graph —
        // HeaderAccountMenu renders inside SiteHeader on nearly every page, and
        // a static import would force every page test to mock them. The sheet
        // fires this in the background (optimistic), and the beacon POSTs with
        // keepalive so the capture survives the user leaving right after "Saved".
        const [{ captureEmailBeacon }, { boardUrlWithResumeToken }] =
          await Promise.all([
            import('../lib/captureEmailBeacon'),
            import('../lib/resumeBoardUrl'),
          ]);
        // Base the emailed link on the RECIPIENT'S BOARD URL, not the current
        // page: this sheet opens from /you or the avatar dropdown, so
        // `window.location.href` is usually /you — and the resume-token redeem
        // leg (`useResumeToken`) mounts ONLY on the board page. Pointing the
        // email at `/quiz/results/{recipientId}/{recommendationId}` lands the
        // `?rt=` link where redeem runs, so it restores the board cross-device.
        // Fall back to the current URL when we can't build a board URL (no
        // recipient / no active recommendation) — capture still works, just
        // without the cross-device restore (today's behavior, no regression).
        const boardBaseUrl =
          typeof window !== 'undefined'
            ? recipientId && recommendationId
              ? `${window.location.origin}/quiz/results/${recipientId}/${recommendationId}`
              : window.location.href
            : undefined;
        // Decorate that board URL with the anon session's resume token (`?rt=`).
        // String-only — the live address bar is never touched (the invariant).
        const boardUrl =
          boardBaseUrl !== undefined
            ? await boardUrlWithResumeToken(boardBaseUrl)
            : undefined;
        await captureEmailBeacon({
          email,
          recipientId,
          recipientName,
          boardUrl,
        });
      }}
      onDismiss={() => {
        gaEmailCaptureDismissed({ surface: AFFORDANCE_SURFACE });
        onClose();
      }}
      onCaptured={() => {
        markEmailCaptured();
        onClose();
      }}
    />
  );
};

export default SaveBoardsSheet;
